/**
 * The requests that *establish* a session, as opposed to those that use one (#34).
 *
 * These must never carry a stale bearer token, and a 401 from one of them is a normal
 * authentication answer — "wrong password", "bad reset link" — not a sign that the current
 * session expired. Treating them as stale-session signals would log the user out of the login
 * page mid-attempt and discard the error message they need to see.
 *
 * Matched on method *and* path, not on an `/auth` prefix: `GET /api/auth?hash=…&token=…` is
 * the SSO login entry point and is excluded, while `PUT /api/auth` (edit profile) is an
 * ordinary authenticated request that must keep its Authorization header.
 */
const AUTH_ENTRY_REQUESTS: ReadonlyArray<{ method: string; path: string }> = [
  {method: 'POST', path: '/auth/login'},
  // Verification failure is owned by AuthService's session-validation path, which does its own
  // cleanup — excluding it here prevents a duplicate cleanup and a double redirect.
  {method: 'POST', path: '/auth/verify'},
  {method: 'POST', path: '/register'},
  {method: 'POST', path: '/forgot-password'},
  {method: 'POST', path: '/reset-password'},
  {method: 'GET', path: '/auth'},
];

const requestPath = (url: string): string => {
  const withoutQuery = (url || '').split('?')[0].split('#')[0];
  return withoutQuery.length > 1 && withoutQuery.endsWith('/')
    ? withoutQuery.slice(0, -1)
    : withoutQuery;
};

export function isAuthEntryRequest(request: { method: string; url: string }): boolean {
  const path = requestPath(request?.url);
  const method = (request?.method || '').toUpperCase();

  return AUTH_ENTRY_REQUESTS.some(entry => entry.method === method && path.endsWith(entry.path));
}
